import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";

const formatPdfCurrency = (v, currency = "EUR") => new Intl.NumberFormat(currency === "CHF" ? "de-CH" : "de-DE", { style: "currency", currency }).format(v || 0);

export const exportTeamReportPdf = (company, month, members) => {
  const eurPdf = (value) => formatPdfCurrency(value, company?.base_currency || "EUR");
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const firma = company?.company_name || "";
  const [y, m] = month.split("-");
  const monatName = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  let yPos = 20;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text(firma || "Provisionsabrechnung", 15, yPos);
  yPos += 8;
  pdf.setFontSize(12);
  pdf.setFont("helvetica", "normal");
  pdf.text(`Provisionsabrechnung – ${monatName}`, 15, yPos);
  yPos += 4;
  pdf.setDrawColor(60);
  pdf.line(15, yPos, 195, yPos);
  yPos += 8;
  const cols = [
    { label: "Mitarbeiter", x: 15, align: "left" },
    { label: "Kunden", x: 86, align: "right" },
    { label: "Offene Pipeline", x: 118, align: "right" },
    { label: "Gewonnen", x: 154, align: "right" },
    { label: "Satz", x: 168, align: "right" },
    { label: "Provision", x: 195, align: "right" },
  ];
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  cols.forEach((c) => pdf.text(c.label, c.x, yPos, { align: c.align }));
  yPos += 2;
  pdf.setDrawColor(180);
  pdf.line(15, yPos, 195, yPos);
  yPos += 6;
  pdf.setFont("helvetica", "normal");
  let sumWon = 0, sumComm = 0;
  members.forEach((mb) => {
    if (yPos > 268) { pdf.addPage(); yPos = 20; }
    const name = mb.name || mb.email || "Nicht zugeordnet";
    pdf.text(String(name).slice(0, 38), 15, yPos);
    const twoLine = Boolean(mb.email && mb.name);
    if (twoLine) {
      pdf.setFontSize(7);
      pdf.setTextColor(120);
      pdf.text(mb.email, 15, yPos + 3.5);
      pdf.setFontSize(9);
      pdf.setTextColor(0);
    }
    pdf.text(String(mb.customers_assigned ?? 0), 86, yPos, { align: "right" });
    pdf.text(eurPdf(mb.open_value), 118, yPos, { align: "right" });
    pdf.text(`${mb.won_count ?? 0}x / ${eurPdf(mb.won_value)}`, 154, yPos, { align: "right" });
    pdf.text(`${mb.commission_rate ?? 0} %`, 168, yPos, { align: "right" });
    pdf.setFont("helvetica", "bold");
    pdf.text(eurPdf(mb.commission), 195, yPos, { align: "right" });
    pdf.setFont("helvetica", "normal");
    sumWon += mb.won_value || 0;
    sumComm += mb.commission || 0;
    yPos += twoLine ? 9 : 7;
  });
  yPos += 1;
  pdf.setDrawColor(60);
  pdf.line(15, yPos, 195, yPos);
  yPos += 6;
  pdf.setFont("helvetica", "bold");
  pdf.text("Gesamt", 15, yPos);
  pdf.text(eurPdf(sumWon), 154, yPos, { align: "right" });
  pdf.text(eurPdf(sumComm), 195, yPos, { align: "right" });
  yPos += 10;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(120);
  pdf.text(`Erstellt am ${new Date().toLocaleDateString("de-DE")} · Provision = gewonnener Deal-Wert im Monat × Provisionssatz`, 15, yPos);
  pdf.save(`Provisionsabrechnung_${month}.pdf`);
};

export const bytesToB64 = (bytes) => {
  let binary = "";
  const arr = new Uint8Array(bytes);
  const chunk = 0x8000;
  for (let i = 0; i < arr.length; i += chunk) {
    binary += String.fromCharCode.apply(null, arr.subarray(i, i + chunk));
  }
  return btoa(binary);
};

// Render a preview DOM node (with [data-pdf-section] children) into an A4 PDF, base64.
export const previewNodeToPdfBase64 = async (node) => {
  if (!node) throw new Error("Vorschau nicht bereit");
  const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
  const SCALE = 2;
  const previewRect = node.getBoundingClientRect();
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const contentW = pageW - margin * 2;
  const usableH = pageH - margin * 2;
  const pxToMm = contentW / canvas.width;
  const SECTION_GAP = 4;
  const cropToDataUrl = (left, top, w, h) => {
    const sub = document.createElement("canvas");
    sub.width = w;
    sub.height = h;
    const ctx = sub.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(canvas, left, top, w, h, 0, 0, w, h);
    return sub.toDataURL("image/jpeg", 0.9);
  };
  const found = Array.from(node.querySelectorAll("[data-pdf-section]"));
  const sectionEls = found.length ? found : [node];
  let cursorY = margin;
  let firstOnPage = true;
  for (const sec of sectionEls) {
    const r = sec.getBoundingClientRect();
    const top = Math.max(0, (r.top - previewRect.top) * SCALE);
    const h = Math.min(canvas.height - top, r.height * SCALE);
    if (h <= 0) continue;
    const secH_mm = h * pxToMm;
    if (!firstOnPage && cursorY + secH_mm > pageH - margin) {
      pdf.addPage();
      cursorY = margin;
      firstOnPage = true;
    }
    if (secH_mm > usableH) {
      let remainingPx = h;
      let offsetPx = 0;
      while (remainingPx > 0) {
        const slicePx = Math.min(remainingPx, Math.floor(usableH / pxToMm));
        const dataUrl = cropToDataUrl(0, top + offsetPx, canvas.width, slicePx);
        const sliceH_mm = slicePx * pxToMm;
        if (!firstOnPage) {
          pdf.addPage();
          cursorY = margin;
        }
        pdf.addImage(dataUrl, "JPEG", margin, cursorY, contentW, sliceH_mm);
        cursorY += sliceH_mm + SECTION_GAP;
        firstOnPage = false;
        remainingPx -= slicePx;
        offsetPx += slicePx;
      }
    } else {
      const dataUrl = cropToDataUrl(0, top, canvas.width, h);
      pdf.addImage(dataUrl, "JPEG", margin, cursorY, contentW, secH_mm);
      cursorY += secH_mm + SECTION_GAP;
      firstOnPage = false;
    }
  }
  return bytesToB64(pdf.output("arraybuffer"));
};

// Capture a full DOM node (e.g. the dashboard) into a multi-page A4 PDF and save it.
export const exportNodeToPdf = async (node, filename = "Report.pdf", title = "") => {
  if (!node) throw new Error("Nichts zum Exportieren");
  const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentW = pageW - margin * 2;
  let headerMm = 0;
  if (title) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(30, 30, 30);
    pdf.text(title, margin, margin + 4);
    headerMm = 10;
  }
  const pxToMm = contentW / canvas.width;
  const usableH = pageH - margin * 2 - headerMm;
  const slicePxMax = Math.floor(usableH / pxToMm);
  let offsetPx = 0;
  let first = true;
  while (offsetPx < canvas.height) {
    const slicePx = Math.min(slicePxMax, canvas.height - offsetPx);
    const sub = document.createElement("canvas");
    sub.width = canvas.width;
    sub.height = slicePx;
    const ctx = sub.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, sub.width, sub.height);
    ctx.drawImage(canvas, 0, offsetPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
    const dataUrl = sub.toDataURL("image/jpeg", 0.92);
    if (!first) pdf.addPage();
    const y = margin + (first ? headerMm : 0);
    pdf.addImage(dataUrl, "JPEG", margin, y, contentW, slicePx * pxToMm);
    offsetPx += slicePx;
    first = false;
  }
  pdf.save(filename);
};
